import { API } from 'aws-amplify';
import { Box, Text, Select, TextInput, Spinner } from 'grommet';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

import Modal from './Modal';
import { CreateUnitInput, CreateUnitMutation } from '../../API';
import { Facility, OrderType, Unit, User } from '../../context/model/types';
import { useUsers } from '../../context/users';
import { createUnit, updateBox, updateOrder, updateTenant, updateUnit, updateFacility, createLedgerEntry } from '../../graphql/mutations';
import StyledButton from '../StyledButton';
import { sendOrderConfirmation } from '../TenantApp/sendOrderConfirmation';
import { dollarsToCents, enqueuePmsJob, moveInBinsID } from '../../integrations/pms';

// TODO: probably want data validation so it's not too big
// prolly on server side

// Pull a numeric monthly rent out of a plan price string like "$30/month".
const parseRent = (price?: string | null): number => {
  const n = parseFloat(String(price ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

// Parse the slot price the customer agreed to at booking time (e.g.
// "$120 flat + $2/min"). Returns null if the string isn't in that shape so
// callers can fall back to the facility's default fees.
const parseChosenSlotPrice = (price?: string | null): { base: number; perMin: number } | null => {
  const m = String(price ?? '').match(/\$(\d+(?:\.\d+)?)\s*flat\s*\+\s*\$(\d+(?:\.\d+)?)\s*\/\s*min/i);
  if (!m) return null;
  const base = parseFloat(m[1]);
  const perMin = parseFloat(m[2]);
  if (!Number.isFinite(base) || !Number.isFinite(perMin)) return null;
  return { base, perMin };
};

// From a 'YYYY-MM-DD' date, return days in that month and days remaining
// including that date — used for the prorated first-month rent.
const getDaysInfo = (dateStr?: string): { daysInMonth: number; daysLeft: number } => {
  const [y, m, d] = String(dateStr ?? '').split('-').map(Number);
  if (!y || !m || !d) return { daysInMonth: 30, daysLeft: 30 };
  const daysInMonth = new Date(y, m, 0).getDate();
  return { daysInMonth, daysLeft: Math.max(0, daysInMonth - d + 1) };
};

interface ModalProps {
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  orderID: number;
}

const FinishOrderModal = ({ setShow, orderID }: ModalProps): JSX.Element => {

  /* eslint-disable */

  const confirmSubmit = async (order: OrderType, selectedUnit: Unit, facility: Facility, tenant: User | undefined, tenantUnits: Unit[]) => {
    if (!tenant) {
      console.error('tenant is undefined, but it should never be so at this point.')
      return;
    }
    
    const orderID = order.id
    const tenantEmail = order.tenantID

    try {
      setIsLoading(true)
     
      // The old SiteLink move-in call lived here (PMS first, abort on failure,
      // with a hardcoded fake card number). It is gone: the PMS push now runs
      // AFTER the Bins writes, through the outbox in src/integrations/pms/, so a
      // vendor outage can never block completing an order. See
      // docs/Integrations/00_pms_adapter_plan.md.
      const unitID = selectedUnit.id
      // const unitID = await grabExistingUnitOrCreateNew(facility, sitelinkUnit, tenantEmail, tenantUnits)
      
      for (let i = 0; i < order.orderItems.length; i+=1) {
        const boxDetail = {id: order.orderItems[i], status: "IN_STORAGE", unitID: unitID}
        await API.graphql({query: updateBox, variables: {input: boxDetail}});
      }
      const orderDetail = {id: orderID, status: "COMPLETED", movingFee: movingFee, movingTime: moveTime}
      await API.graphql({query: updateOrder, variables: {input: orderDetail}});
      const unitDetail = {id: unitID, tenantID: tenantEmail, usage: usageValue, availability: 'RENTED'}
      await API.graphql({query: updateUnit, variables: {input: unitDetail}});
      const tenantDetail = {id: tenantEmail, unitID: unitID}
      await API.graphql({query: updateTenant, variables: {input: tenantDetail}});

      // Record the charge in the ledger (prorated rent + moving fee from the calculator).
      const ledgerType = proratedRent > 0 && movingFee > 0
        ? 'Rent + move'
        : proratedRent > 0 ? 'Rent' : 'Moving fee';
      await API.graphql({
        query: createLedgerEntry,
        variables: {
          input: {
            id: uuidv4(),
            facilityID: facility.id,
            name: tenant.name,
            tenantID: tenant.id,
            date: new Date().toISOString().slice(0, 10),
            amount: Number(totalCharge.toFixed(2)),
            type: ledgerType,
            month: (order.date || '').slice(0, 7) || new Date().toISOString().slice(0, 7),
            notes: `Order ${String(order.id).slice(0, 8)}`,
            paid: false,
            // Rent-only share of this combined charge. The PMS posts rent and
            // not our valet fees, which would exceed the facility's amount due.
            rentPortionCents: proratedRent > 0 ? dollarsToCents(proratedRent) : 0,
          },
        },
      });

      // Mirror the move-in into the facility's storage software. Runs AFTER the
      // Bins writes and never blocks them — the old SiteLink call did the
      // opposite and could abandon a half-finished order.
      try {
        await enqueuePmsJob({
          facilityID: facility.id,
          op: 'moveIn',
          binsID: moveInBinsID(tenant.id, String(unitID)),
          payload: {
            binsTenantID: tenant.id,
            // Unit.id is typed as a number in the app model but is an AppSync ID.
            binsUnitID: String(unitID),
            startDateISO: order.date || new Date().toISOString().slice(0, 10),
            // Our rate, which overrides the facility's board rate so both
            // ledgers agree from day one.
            rentAmountCents: monthlyRent > 0 ? dollarsToCents(monthlyRent) : 0,
            // Nothing is collected here: the ledger entry above is unpaid, and
            // the payment is posted separately once it is marked paid. Shadow
            // mode will show whether SSM accepts a zero-amount move-in.
            amountCents: 0,
            tenantName: tenant.name,
          },
        });
      } catch (pmsError) {
        console.warn('[pms] moveIn enqueue failed:', pmsError);
      }

      // Post-move confirmation email — only for initial move-ins (the template
      // wording is move-in specific). Best-effort, never blocks completion.
      if (order.isInitialOrder) {
        try {
          await sendOrderConfirmation({
            email: tenant.id,
            name: tenant.name,
            monthlyRent,
            laborMinutes: moveTime,
            moveInCost: movingFee,
            base: moveBase,
            perMin,
          });
        } catch (e) {
          console.warn('Order confirmation email failed:', e);
        }
      }

      setShow(false);
      window.location.reload();
    } catch (e: unknown) {
      alert('An internal error occurred while updating the unit - please try again!')
      console.error(e);
    }
  };

  // If the given sitelink unit id belongs to an existing unit, returns that unit. Else, returns a newly created unit.
  {/*const grabExistingUnitOrCreateNew = async (facility: Facility, sitelinkUnit: SitelinkUnit, tenantID: string, tenantUnits: Unit[]): Promise<string> => {
    const maybeTenantUnit = tenantUnits.find(unit => unit.sitelinkUnit.UnitID === sitelinkUnit.UnitID)
    if (maybeTenantUnit) {
      return maybeTenantUnit.id.toString()  // Definitely tenant's unit
    }

    // Make a new unit if sitelink unit ID does not belong to an existing unit.
    const newUnit = {
      id: sitelinkUnit.UnitID.toString(),
      facilityID: facility.id,
      sitelinkUnit: sitelinkUnit,
      size: Math.trunc(sitelinkUnit.dcLength) + 'x' + Math.trunc(sitelinkUnit.dcWidth),
      usage: '0%',
      price: '$' + Math.trunc(sitelinkUnit.dcStdRate).toString(), 
      tenantID: tenantID,
    } as CreateUnitInput

    try {
      console.log('trying to type assert')
      const a = newUnit.sitelinkUnit as SitelinkUnitInput
      console.log('success')
    } catch (e: unknown) {
      console.log('err: ', e)
    }
    try {
      // Create the unit
      console.log(newUnit)
      const unit = (await API.graphql({query: createUnit, variables: {input: newUnit}})) as {data: CreateUnitMutation}
      // TODO(dynamodb): add this to the facility in dynamodb too
      if (!unit.data.createUnit) {
        throw new Error('DynamoDB CreateUnitMutation Failed')
      }
      return unit.data.createUnit.id
    } catch (e: unknown) {  // TODO: rethrowing an error is a codesmell. Handle it and return appropriately.
      console.error('Error creating unit: ', e)
      const err = e as {data: any, errors: Array<any>}
      console.log('Error creating unitt: ', err.errors[0]);
      throw e;
    }
  }*/}

  /* eslint-enable */
  const [isLoading, setIsLoading] = React.useState(false);
    // Removed sitelinkUnits from context - now using units only
  const { orders, units, facilityInfo, users } = useUsers();
  
  const orderMetadata = orders.filter(order => order.id === orderID);  
  const tenant = users.find(user => orderMetadata[0].tenantID === user.id)
  console.log('is tenant null: ', tenant);

  // Get tenant's existing units
  const tenantUnits = units.filter(unit => unit.tenantID === orderMetadata[0].tenantID);
  
  // Get available units based on whether this is an initial order
  // For initial orders: show all unrented units
  // For subsequent orders: show tenant's existing units
  const unrentedUnits = units.filter(unit => unit.availability === 'VACANT');
  const availableUnits = orderMetadata[0].isInitialOrder ? unrentedUnits : tenantUnits;
  const [selectedUnit, setSelectedUnit] = React.useState(availableUnits[0]);
  const [usageValue, setUsage] = React.useState(orderMetadata[0].isInitialOrder ? "0%" : tenantUnits[0].usage)

  console.log('unrented units: ', unrentedUnits);
  console.log('available units: ', availableUnits);

  console.log("FacilityIno:", facilityInfo)
  console.log("orderInfo:", orders)
  const truckFee = facilityInfo?.movingCosts.flatFee
  const hourlyFee = facilityInfo?.movingCosts.hourlyFee
  const effectiveTruckFee = truckFee !== undefined ? truckFee : 0;
  const effectiveHourlyFee = hourlyFee !== undefined ? hourlyFee : 0;

  // ── Charge calculator: total = prorated rent (days left this month) + moving fee ──
  const { daysInMonth, daysLeft: defaultDaysLeft } = getDaysInfo(orderMetadata[0].date);
  // Prefer the slot price the customer agreed to at booking. Fall back to the
  // facility's default fees if nothing was stamped on the order.
  const chosenSlot = parseChosenSlotPrice(orderMetadata[0].chosenSlotPrice);
  const defaultMoveBase = chosenSlot ? chosenSlot.base : effectiveTruckFee;
  const defaultPerMin = chosenSlot ? chosenSlot.perMin : Number((effectiveHourlyFee / 60).toFixed(2));
  const [monthlyRent, setMonthlyRent] = React.useState(parseRent(tenant?.plan?.price));
  const [daysLeft, setDaysLeft] = React.useState(defaultDaysLeft);
  const [moveBase, setMoveBase] = React.useState(defaultMoveBase);
  const [moveTime, setMoveTime] = React.useState(orderMetadata[0].movingTime || 0);
  const [perMin, setPerMin] = React.useState(defaultPerMin);

  const proratedRent = daysInMonth > 0 ? (monthlyRent * daysLeft) / daysInMonth : 0;
  const movingFee = moveBase + moveTime * perMin;
  const totalCharge = proratedRent + movingFee;

  if (!facilityInfo) {
    return (
      <Modal setShow={setShow}>
          <Text>Unable to find an available unit!</Text>
      </Modal>
    )
  }
  const facility = facilityInfo;

  if (!selectedUnit || availableUnits.length === 0) {
    return (
      <Modal setShow={setShow}>
        <Text>Unable to find an available unit!</Text>
      </Modal>
    );
  }

  return (
    <Modal
      setShow={setShow}
      header="Finish Order"
    >
    { isLoading ? (
    <>
      <Spinner/>
    </> ) : ( 
    <>
    {orderMetadata.map(order => (
      <Box gap="medium" pad={{ horizontal: 'large' }}>
        <Box direction="column" gap="medium">
          <Box height={{ max: 'large' }} overflow={{ vertical: 'auto' }} gap="small">
            <Text weight="bold">
              Name:{' '}
              <Text weight="normal" color="dark-3">
                {order.tenantID}
              </Text>
            </Text>
            <Text weight="bold">
              Address:{' '}
              <Text weight="normal" color="dark-3">
                {order.address.addressLine1 + ", " + order.address.city + ", " + order.address.state}
              </Text>
            </Text>
            <Text weight="bold">
              Special Instructions:{' '}
              <Text weight="normal" color="dark-3">
                {order.address.specialInstructions || '—'}
              </Text>
            </Text>
            <Text weight="bold">
              Mover Option:{' '}
              <Text weight="normal" color={order.moverOption === 'Itemized' ? 'status-ok' : 'dark-3'}>
                {order.moverOption || '—'}
              </Text>
            </Text>
            <Text weight="bold">
              Date:{' '}
              <Text weight="normal" color="dark-3">
                {order.date}
              </Text>
            </Text>
            <Text weight="bold">
              Time:{' '}
              <Text weight="normal" color="dark-3">
                {order.time}
              </Text>
            </Text>
            <Text weight="bold">
              Driver:{' '}
              <Text weight="normal" color="dark-3">
                {order.driver}
              </Text>
            </Text>
            <Text weight="bold">
              Items:{' '}
              <Text weight="normal" color="dark-3">
                {order.orderItems.toString()}
              </Text>
            </Text>
            {order.isInitialOrder && tenant && (
              <Text weight="bold">
                Requested Size:{' '}
                <Text weight="normal" color="dark-3">
                  {tenant.plan.size}
                </Text>
              </Text>
            )}
            {!order.isInitialOrder && order.chosenSlotPaid !== null && order.chosenSlotPaid !== undefined && (
              <Text weight="bold">
                Customer chose:{' '}
                <Text weight="normal" color={order.chosenSlotPaid ? 'status-warning' : 'dark-3'}>
                  {order.chosenSlotPaid ? `Paid (${order.chosenSlotPrice ?? 'paid slot'})` : 'Free'}
                </Text>
              </Text>
            )}

            {/* ── Charge calculator ── */}
            <Box gap="xsmall" pad={{ top: 'small' }} border={{ side: 'top', color: 'light-4' }}>
              <Text weight="bold">Amount to charge</Text>

              <Box direction="row" align="center" gap="xsmall" wrap>
                <Text size="small">Monthly rent $</Text>
                <Box width="70px">
                  <TextInput
                    type="number"
                    size="small"
                    value={monthlyRent}
                    onChange={event => setMonthlyRent(event.target.valueAsNumber || 0)}
                  />
                </Box>
                <Text size="small">· days left</Text>
                <Box width="70px">
                  <TextInput
                    type="number"
                    size="small"
                    value={daysLeft}
                    onChange={event => setDaysLeft(event.target.valueAsNumber || 0)}
                  />
                </Box>
                <Text size="small">of {daysInMonth} (from {order.date})</Text>
              </Box>
              <Text size="small" color="dark-3">
                Prorated rent: ${proratedRent.toFixed(2)}
              </Text>

              <Box direction="row" align="center" gap="xsmall" wrap>
                <Text size="small">Moving base $</Text>
                <Box width="70px">
                  <TextInput
                    type="number"
                    size="small"
                    value={moveBase}
                    onChange={event => setMoveBase(event.target.valueAsNumber || 0)}
                  />
                </Box>
                <Text size="small">+ time</Text>
                <Box width="70px">
                  <TextInput
                    type="number"
                    size="small"
                    value={moveTime}
                    onChange={event => setMoveTime(event.target.valueAsNumber || 0)}
                  />
                </Box>
                <Text size="small">min × $</Text>
                <Box width="70px">
                  <TextInput
                    type="number"
                    size="small"
                    value={perMin}
                    onChange={event => setPerMin(event.target.valueAsNumber || 0)}
                  />
                </Box>
                <Text size="small">/min</Text>
              </Box>
              <Text size="small" color="dark-3">
                Moving fee: ${movingFee.toFixed(2)}
              </Text>

              <Text weight="bold">
                Total to charge:{' '}
                <Text weight="bold" color="status-ok">
                  ${totalCharge.toFixed(2)}
                </Text>
              </Text>
            </Box>
            <Box direction='row' align="center">
            <Text weight="bold">
              Usage:{' '}
            </Text>
                <Box>
                <TextInput
                  value={usageValue}
                  onChange={event => setUsage(event.target.value)}
                />
                </Box>
            </Box>
            <Text weight="bold">
              Unit: {' '}
              <Select
                options={availableUnits}
                labelKey={option => {
                  // Use the Unit's size property directly from DynamoDB
                  return `${option.unitID} - ${option.size}`;
                }}
                valueKey='unitID'
                value={selectedUnit}
                size='small'
                onChange={({ option }) => {setSelectedUnit(option)}}
                plain={true}
              />
            </Text>
          </Box>
        </Box>
        <Box direction="row" justify="center">
          <StyledButton label="Approve" onClick={() => confirmSubmit(order, selectedUnit, facility, tenant, tenantUnits)} />
        </Box>
      </Box>
      ))
     }
     </>
    )}
    </Modal>
  );
};

export default FinishOrderModal;
